/**
 * 极简的前端路由（History API）。
 *
 * 背景：控制台的页面地址会被外部跳进来：OAuth 授权跳 /login?return_to=… 与 /consent?request_id=…，
 *   Stripe 付款后跳回 /wallet?paid=1&order=…，所以必须用真实路径，不能用 #hash 路由。
 * 设计意图：页面不到 20 个，只需要「当前路径 + 查询参数 + 跳转 + 带参数的路径匹配」，
 *   自己写 60 行，省掉一个路由库依赖。
 * 关键约束：页面路径不能落在接口前缀下（/auth /me /oauth /control /billing /admin /api），
 *   入口负载均衡按这些前缀把请求分给后端服务。
 */
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react'

const EVENT = 'ap:navigate'

function subscribe(fn: () => void): () => void {
  window.addEventListener('popstate', fn)
  window.addEventListener(EVENT, fn)
  return () => {
    window.removeEventListener('popstate', fn)
    window.removeEventListener(EVENT, fn)
  }
}

const snapshot = () => window.location.pathname + window.location.search

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === snapshot()) return
  if (opts.replace) window.history.replaceState(null, '', to)
  else window.history.pushState(null, '', to)
  window.dispatchEvent(new Event(EVENT))
  if (!opts.replace) window.scrollTo(0, 0)
}

export interface RouteState {
  path: string
  query: URLSearchParams
}

export function useRoute(): RouteState {
  const full = useSyncExternalStore(subscribe, snapshot)
  const i = full.indexOf('?')
  const path = i < 0 ? full : full.slice(0, i)
  return { path: path.replace(/\/+$/, '') || '/', query: new URLSearchParams(i < 0 ? '' : full.slice(i + 1)) }
}

/** 匹配 /accounts/:id 这类模式，返回参数；不匹配返回 null。 */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean)
  const a = path.split('/').filter(Boolean)
  if (p.length !== a.length) return null
  const out: Record<string, string> = {}
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) out[p[i].slice(1)] = decodeURIComponent(a[i])
    else if (p[i] !== a[i]) return null
  }
  return out
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    if (rest.target && rest.target !== '_self') return
    e.preventDefault()
    navigate(href)
  }
  return <a href={href} onClick={handle} {...rest} />
}
